import React, { useState, useEffect, useCallback } from 'react';
import ImageThumbnail from './ImageThumbnail';
import './ImageGrid.css';

const ImageGrid = ({ files, imageMetadata, imageVersions, selectedImageIds, onToggleSelection, disabled, onFilesDropped, onUrlsDropped, onImageClick, onSelectFiles, onShowInFolder, onDeleteFile }) => {
  const [columns, setColumns] = useState(4);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    const updateColumns = () => {
      const width = window.innerWidth;
      if (width >= 1400) setColumns(6);
      else if (width >= 1200) setColumns(5);
      else if (width >= 900) setColumns(4);
      else if (width >= 600) setColumns(3);
      else setColumns(2);
    };

    updateColumns();
    window.addEventListener('resize', updateColumns);
    return () => window.removeEventListener('resize', updateColumns);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget === e.target) {
      setIsDragging(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    // HEIC and TGA have an empty MIME type on Windows — match by extension too
    const files = Array.from(e.dataTransfer.files).filter(file =>
      file.type.startsWith('image/') || /\.(heic|heif|tga)$/i.test(file.name)
    );

    if (files.length > 0 && onFilesDropped) {
      onFilesDropped(files);
      return;
    }

    // Browser drags sometimes carry only a URL (no File payload) —
    // e.g. dragging from image search results or certain sites.
    const uriList = e.dataTransfer.getData('text/uri-list') || e.dataTransfer.getData('text/plain');
    const urls = (uriList || '')
      .split(/\r?\n/)
      .map(s => s.trim())
      .filter(u => u && !u.startsWith('#') && /^(https?:\/\/|data:image\/)/i.test(u));
    if (urls.length > 0 && onUrlsDropped) {
      onUrlsDropped(urls);
    }
  }, [onFilesDropped, onUrlsDropped]);


  if (files.length === 0) {
    return (
      <div 
        className={`image-grid glass-card ${isDragging ? 'dragging' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={onSelectFiles}
        style={{ cursor: 'pointer' }}
      >
        <div className="empty-state">
          <div className="empty-state-icon">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
              <path 
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" 
                stroke="#FE8706" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <p className="empty-state-description">
            {isDragging ? 'Drop your images here!' : 'Drag and drop images here or click to browse'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`image-grid glass-card-dark ${isDragging ? 'dragging' : ''}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div className="grid-header">
        <h3 className="grid-title">
          Images ({files.length})
        </h3>
      </div>
      
      <div 
        className="image-grid-container"
        style={{ '--columns': columns }}
      >
        {files.map((filePath, index) => (
          <ImageThumbnail
            key={filePath}
            filePath={filePath}
            metadata={imageMetadata[filePath]}
            isSelected={selectedImageIds.has(filePath)}
            onToggleSelection={(isSelected) => onToggleSelection(filePath, isSelected)}
            onImageClick={() => onImageClick && onImageClick(index)}
            onShowInFolder={onShowInFolder}
            onDeleteFile={onDeleteFile}
            disabled={disabled}
            index={index}
            version={imageVersions ? imageVersions[filePath] : undefined}
          />
        ))}
      </div>
    </div>
  );
};

export default ImageGrid;